import React, { useRef, useState } from 'react';

import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, LinearProgress, useTheme } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';


function StorageUploadModal({ open, onClose, onUpload }) {
  const theme = useTheme();
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files);
    if (selected.length) {
      setFiles(selected);
      setError('');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer.files);
    if (dropped.length) {
      setFiles(dropped);
      setError('');
    }
  };

  const handleUpload = async () => {
    if (!files.length) return setError('Please select files.');
    setProgress(0);
    setError('');
    setUploading(true);
    for (let i = 0; i < files.length; i++) {
      await onUpload(files[i], p => setProgress(Math.round((i * 100 + p) / files.length)), setError);
    }
    setFiles([]);
    setProgress(0);
    setUploading(false);
  };

  const handleClose = () => {
    setFiles([]);
    setProgress(0);
    setError('');
    setUploading(false);
    onClose();
  };

  // File preview logic for all files
  let previews = null;
  if (files.length) {
    previews = (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {files.map((file, idx) => {
          let preview = null;
          if (file.type && file.type.startsWith('image/')) {
            // Actual image thumbnail, larger
            preview = (
              <Box sx={{ textAlign: 'center', width: '100%' }}>
                <img src={URL.createObjectURL(file)} alt={file.name} style={{ maxWidth: 220, maxHeight: 220, borderRadius: 10, boxShadow: 2, width: 'auto', height: 'auto' }} />
                <Typography variant="caption" color="text.secondary">Image</Typography>
              </Box>
            );
          } else if (file.type === 'application/pdf') {
            // PDF preview (first page), larger
            preview = (
              <Box sx={{ textAlign: 'center', width: '100%' }}>
                <iframe
                  src={URL.createObjectURL(file)}
                  title={file.name}
                  style={{ width: 220, height: 220, border: 'none', borderRadius: 10, boxShadow: 2 }}
                />
                <Typography variant="caption" color="text.secondary">PDF Preview</Typography>
              </Box>
            );
          } else if (file.type && file.type.startsWith('text/')) {
            // Text file preview (first 8 lines), larger
            const [text, setText] = React.useState('');
            React.useEffect(() => {
              const reader = new FileReader();
              reader.onload = e => setText(e.target.result.split('\n').slice(0, 8).join('\n'));
              reader.readAsText(file);
            }, [file]);
            preview = (
              <Box sx={{ textAlign: 'center', maxWidth: 220, width: '100%' }}>
                <InsertDriveFileIcon color="action" sx={{ fontSize: 44 }} />
                <Typography variant="caption" color="text.secondary">Text Preview</Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 1, whiteSpace: 'pre-line', fontSize: 14 }}>{text}</Typography>
              </Box>
            );
          } else if (file.type && (file.type.includes('word') || file.name.endsWith('.doc') || file.name.endsWith('.docx'))) {
            // Word/docx preview (embed if possible), larger
            preview = (
              <Box sx={{ textAlign: 'center', width: '100%' }}>
                <iframe
                  src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(URL.createObjectURL(file))}`}
                  title={file.name}
                  style={{ width: 220, height: 220, border: 'none', borderRadius: 10, boxShadow: 2 }}
                />
                <Typography variant="caption" color="text.secondary">Word Preview</Typography>
              </Box>
            );
          } else {
            // Generic file icon, larger
            preview = (
              <Box sx={{ textAlign: 'center', width: '100%' }}>
                <InsertDriveFileIcon color="action" sx={{ fontSize: 44 }} />
                <Typography variant="caption" color="text.secondary">{file.type || 'File'}</Typography>
              </Box>
            );
          }
          return (
            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {preview}
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{file.name}</Typography>
                <Typography variant="caption" color="text.secondary">{(file.size / 1024 / 1024).toFixed(2)} MB</Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{
      sx: {
        borderRadius: 4,
        boxShadow: 8,
        bgcolor: theme.palette.mode === 'dark' ? '#181c24' : '#fff',
        color: theme.palette.text.primary,
        p: 0
      }
    }}>
      <DialogTitle sx={{
        bgcolor: theme.palette.mode === 'dark' ? '#23293a' : '#f5faff',
        color: theme.palette.text.primary,
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        fontWeight: 700,
        fontSize: 22,
        px: 4,
        py: 2.5
      }}>Upload Files</DialogTitle>
      <DialogContent sx={{
        px: { xs: 2, sm: 4 },
        py: { xs: 2, sm: 3 },
        bgcolor: theme.palette.mode === 'dark' ? '#181c24' : '#fff',
        borderBottomLeftRadius: 16,
        borderBottomRightRadius: 16
      }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'flex-start', gap: 3 }}>
          <Box
            onDrop={handleDrop}
            onDragOver={e => e.preventDefault()}
            sx={{
              border: `2px dashed ${theme.palette.primary.light}`,
              borderRadius: 3,
              p: 3,
              textAlign: 'center',
              mb: { xs: 2, sm: 0 },
              cursor: 'pointer',
              bgcolor: theme.palette.mode === 'dark' ? '#23293a' : '#f5faff',
              flex: 1,
              minWidth: 0,
              transition: 'background 0.2s',
              '&:hover': { bgcolor: theme.palette.mode === 'dark' ? '#23293a' : '#e3f2fd' }
            }}
            onClick={() => inputRef.current.click()}
          >
            <CloudUploadIcon color="primary" sx={{ fontSize: 54, mb: 1 }} />
            <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>Drag & drop or click to select files</Typography>
            <input type="file" hidden ref={inputRef} onChange={handleFileChange} multiple />
            {files.length > 0 && <Typography variant="body2" sx={{ mt: 1 }}>{files.length} file(s) selected</Typography>}
          </Box>
          {files.length > 0 && (
            <Box sx={{
              flex: 2,
              minWidth: { xs: '100%', sm: 340 },
              maxWidth: { xs: '100%', sm: 600 },
              maxHeight: 400,
              overflowY: 'auto',
              bgcolor: theme.palette.mode === 'dark' ? '#23293a' : '#f8fafd',
              borderRadius: 3,
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              boxShadow: 2
            }}>{previews}</Box>
          )}
        </Box>
        {progress > 0 && <LinearProgress variant="determinate" value={progress} sx={{ mb: 1, mt: 2 }} />}
        {error && <Typography color="error" variant="body2" sx={{ mt: 1 }}>{error}</Typography>}
      </DialogContent>
      <DialogActions sx={{ px: 4, py: 2, bgcolor: theme.palette.mode === 'dark' ? '#23293a' : '#f5faff', borderBottomLeftRadius: 16, borderBottomRightRadius: 16 }}>
        <Button onClick={handleClose} disabled={uploading} sx={{ borderRadius: 2, fontWeight: 600 }}>Cancel</Button>
        <Button onClick={handleUpload} variant="contained" disabled={!files.length || uploading} sx={{ borderRadius: 2, fontWeight: 700 }}>Upload</Button>
      </DialogActions>
    </Dialog>
  );
}

export default StorageUploadModal;
